import axiosInstance from '../config/axios.js';
import {
  PDF_RESOLVE_CHUNK_SIZE,
  buildPdfPageChunks,
  estimatePdfPageCount,
  mergeQrResolveChunkResponses,
} from '../utils/pdfScanChunks.js';

const API_BASE = '/shipment-qr-status';

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');

const appendScanFile = (formData, file) => {
  if (!(file instanceof File)) return;
  formData.append(isPdfFile(file) ? 'scanned_pdf' : 'scanned_image', file, file.name);
};

const appendPayload = (formData, payload = {}) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (key === 'shipment_ids' && Array.isArray(value)) {
      formData.append('shipment_ids', JSON.stringify(value));
      return;
    }
    if (
      (key === 'receiver_agent_by_shipment' || key === 'verified_units_by_shipment')
      && value
      && typeof value === 'object'
    ) {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, value);
  });
};

const QR_UPLOAD_TIMEOUT_MS = 90000;

const postResolveFile = async (file, { pageStart, pageEnd } = {}) => {
  const formData = new FormData();
  appendScanFile(formData, file);
  if (pageStart != null) formData.append('pdf_page_start', String(pageStart));
  if (pageEnd != null) formData.append('pdf_page_end', String(pageEnd));
  const response = await axiosInstance.post(`${API_BASE}/resolve`, formData, {
    headers: { 'Content-Type': undefined },
    timeout: QR_UPLOAD_TIMEOUT_MS,
  });
  return response.data;
};

const resolvePdfInChunks = async (file) => {
  const buffer = await file.arrayBuffer();
  const pageCount = estimatePdfPageCount(buffer);
  const chunks = buildPdfPageChunks(pageCount, PDF_RESOLVE_CHUNK_SIZE);

  if (chunks.length === 1) {
    const only = chunks[0];
    return postResolveFile(file, {
      pageStart: pageCount > 1 ? only.start : undefined,
      pageEnd: pageCount > 1 ? only.end : undefined,
    });
  }

  const parts = [];
  for (const chunk of chunks) {
    parts.push(await postResolveFile(file, { pageStart: chunk.start, pageEnd: chunk.end }));
  }
  return mergeQrResolveChunkResponses(parts);
};

const shipmentQrStatusService = {
  resolveFromQr: async (fileOrShipmentId) => {
    if (fileOrShipmentId instanceof File) {
      if (isPdfFile(fileOrShipmentId)) {
        return resolvePdfInChunks(fileOrShipmentId);
      }
      return postResolveFile(fileOrShipmentId);
    }

    const response = await axiosInstance.post(`${API_BASE}/resolve`, {
      shipment_id: fileOrShipmentId,
    });
    return response.data;
  },

  /** Resolve from app live QR decode (raw QR string or shipment id). */
  resolveFromQrData: async ({ qrData, shipmentId, shipmentIds, unitNo } = {}) => {
    const payload = {};
    // Always send raw QR when present so parcel unit (?u=N) is not dropped.
    if (qrData) payload.qr_data = qrData;
    if (shipmentIds?.length) payload.shipment_ids = shipmentIds;
    else if (shipmentId) payload.shipment_id = shipmentId;
    if (unitNo != null && unitNo !== '') payload.unit_no = unitNo;
    if (!payload.qr_data && !payload.shipment_id && !payload.shipment_ids) {
      throw new Error('No QR data to resolve');
    }

    const response = await axiosInstance.post(`${API_BASE}/resolve`, payload);
    return response.data;
  },

  listTodayMlSheets: async (params = {}) => {
    const response = await axiosInstance.get(`${API_BASE}/today-ml-sheets`, { params });
    return response.data;
  },

  dispatchOntoMlSheet: async ({
    shipmentIds,
    mlSheetId,
    verified_units_by_shipment,
    allow_legacy_qr,
  }) => {
    const response = await axiosInstance.post(`${API_BASE}/dispatch`, {
      shipment_ids: shipmentIds,
      ml_sheet_id: mlSheetId,
      verified_units_by_shipment,
      allow_legacy_qr: allow_legacy_qr ? true : undefined,
    });
    return response.data;
  },

  validateDispatch: async ({ shipment_id, ml_sheet_id }) => {
    const response = await axiosInstance.post(`${API_BASE}/validate-dispatch`, {
      shipment_id,
      ml_sheet_id,
    });
    return response.data;
  },

  updateStatusFromQr: async (payload = {}, extraFiles = {}) => {
    const deliveryImage = extraFiles.delivery_receiver_image;
    const deliveryPhoto = extraFiles.delivery_photo;

    if (deliveryImage instanceof File || deliveryPhoto instanceof File) {
      const formData = new FormData();
      appendPayload(formData, payload);
      if (deliveryImage instanceof File) {
        formData.append('delivery_receiver_image', deliveryImage);
      }
      if (deliveryPhoto instanceof File) {
        formData.append('delivery_photo', deliveryPhoto);
      }
      const response = await axiosInstance.post(`${API_BASE}/update-status`, formData, {
        headers: { 'Content-Type': undefined },
        timeout: QR_UPLOAD_TIMEOUT_MS,
      });
      return response.data;
    }

    const response = await axiosInstance.post(`${API_BASE}/update-status`, payload);
    return response.data;
  },
};

export default shipmentQrStatusService;
